import { BasicIndex, type Collection } from "@tanstack/db";

import { recordRead } from "@/store/readTracking";

const EMPTY_KEYS: ReadonlySet<string> = new Set();

/**
 * Incrementally maintained secondary index: groups row keys by a single indexed field (e.g. items
 * by feedId). Backed by a TanStack collection index, which the live-query engine also uses for
 * predicate evaluation. Lookups return row keys; resolve rows through the store.
 */
export type EqualityIndex = {
  /** Row keys whose indexed field equals `value`; the empty set for null/undefined. */
  keys(value: string | null | undefined): ReadonlySet<string>;
};

/**
 * Creates an equality index on `field`. TanStack maintains it through upserts, re-keys, deletes,
 * and truncates, and builds it from current rows when created after data has loaded.
 *
 * A lookup records a read of the indexed collection. `rowsByIndex` resolves rows through the store
 * and would be attributed there anyway, but callers that only count keys never touch a row, and
 * neither does a lookup that matches none.
 */
export function createEqualityIndex<T extends Record<string, unknown>>(
  collection: Collection<T, string>,
  field: keyof T & string,
): EqualityIndex {
  const index = collection.createIndex((row) => row[field], { indexType: BasicIndex });
  if (!(index instanceof BasicIndex)) {
    throw new Error(`Expected a BasicIndex for field: ${field}`);
  }
  return {
    keys: (value) => {
      recordRead(collection.id);
      return value == null ? EMPTY_KEYS : (index.equalityLookup(value) as ReadonlySet<string>);
    },
  };
}
